import React, { useEffect } from 'react'
import { RouteComponentProps, Redirect } from 'react-router-dom'

// Constants
import {
  GenericErrorView,
  routes,
  RouteParams,
  useUserProfile,
  useFlag,
} from '@sketch/modules-common'

// Components
import { WelcomeModalTrigger } from 'modules/workspace/components'
import { LoadingState } from '@sketch/components'
import { Discover, DiscoverNavBar, useDiscover } from '@sketch/discover'

// Types
import {
  useGetWorkspaceBillingQuery,
  useGetDiscoverQuery,
} from '@sketch/gql-types'
import { WorkspaceSidebarLayoutExtraProps } from 'modules/workspace/components/WorkspaceSidebarLayout/WorkspaceSidebarLayout'

import { Iframe } from './DiscoverView.styles'

export type DiscoverViewRoutesProps = RouteComponentProps<
  RouteParams<'WORKSPACE_DISCOVER'>
>

type DiscoverViewProps = DiscoverViewRoutesProps &
  WorkspaceSidebarLayoutExtraProps

/**
 * Discover View
 *
 * Renders the view for the Discover page
 * Route: /workspace/{workspaceId}/discover
 */
const DiscoverView = (props: DiscoverViewProps) => {
  const { workspace, NavbarPortal, useOverrideLayoutProps } = props
  const isEmbeddedDiscover = useFlag('embedded-discover')

  useOverrideLayoutProps({
    title: 'Discover',
  })

  // Sets discover content hash to local storage
  const { setViewedDiscover } = useDiscover()

  useEffect(() => {
    setViewedDiscover()
  }, [setViewedDiscover])

  const { data: userProfile, loading: isUserProfileLoading } = useUserProfile()

  const {
    data: discoverData,
    loading: discoverLoading,
    error: discoverError,
  } = useGetDiscoverQuery()

  const billingQuery = useGetWorkspaceBillingQuery({
    variables: { identifier: workspace.identifier },
    skip: !workspace?.customer?.identifier,
  })

  if (isUserProfileLoading || discoverLoading) {
    return <LoadingState />
  }

  if (discoverError || !discoverData) {
    return <GenericErrorView isInLayout />
  }

  const isFinance = workspace!.userRole === 'FINANCE'

  if (isFinance) {
    return <Redirect to={routes.ENTRY.create({})} />
  }

  const subscription = billingQuery.data?.workspace?.customer?.billing
  const professionalRole = userProfile?.me.professionalRole || 'DESIGNER'

  return (
    <>
      <NavbarPortal>
        <DiscoverNavBar />
      </NavbarPortal>

      {isEmbeddedDiscover ? (
        <Iframe
          title="Sketch Discover"
          src="https://deploy-preview-4456--sketchapp.netlify.app/discover/?source=web-app"
        />
      ) : (
        <Discover
          data={discoverData}
          professionalRole={professionalRole}
          workspace={workspace}
        />
      )}

      {subscription && (
        <WelcomeModalTrigger
          subscriptionDetails={subscription}
          workspace={workspace}
        />
      )}
    </>
  )
}

export default DiscoverView
